import { type FC } from "react";
import { makeStyles } from "tss-react/mui";
import {
  AutocompleteOption,
  MiAutocomplete,
  MiButton,
  MiDatePicker,
} from "@miplatform/components";
import { BUTTON_COLORS, BUTTON_VARIANTS } from "@miplatform/constants";
import {
  AccountType,
  IAccountListResponse,
  ICommunitiesResponse,
} from "@miplatform/types";
import { getDate } from "@miplatform/utils";
import { ServerAutocomplete } from "@mibuildweb/components";
import { useAccounts, useCommunities } from "@mibuildweb/hooks";
import { Box } from "@mui/material";
import type { CalendarFilterGroupController } from "./useCalendarFilterGroup";

export const CalendarFilterGroup: FC<CalendarFilterGroupProps> = ({
  filters,
  statusOptions,
  hasPendingFilters,
  onApplyFilters,
  onResetFilters,
  onToggleFilters,
  handleFilterChange,
  layout = "inline",
}) => {
  const { classes, cx } = useStyles();
  const { communities, getCommunities } = useCommunities();
  const { accounts, getAccounts } = useAccounts({
    accountTypes: [AccountType.Builder],
  });

  const isInline = layout === "inline";
  const fieldClassName = isInline ? classes.inlineField : classes.stackedField;

  const fields = (
    <>
      <Box className={fieldClassName}>
        <MiAutocomplete
          name="calendar-filter-status"
          label="Status"
          options={statusOptions}
          value={filters.status}
          getOptionLabel={(option: AutocompleteOption) => option.label}
          onChange={(_, value) => handleFilterChange("status", value)}
        />
      </Box>

      <Box className={fieldClassName}>
        <ServerAutocomplete<IAccountListResponse>
          name="calendar-filter-builder"
          label="Builder"
          value={filters.builder}
          labelKey="name"
          filterKeys={["name"]}
          options={accounts}
          getter={getAccounts}
          onChange={(value) => handleFilterChange("builder", value)}
          sx={{ width: "100%" }}
        />
      </Box>

      <Box className={fieldClassName}>
        <ServerAutocomplete<ICommunitiesResponse>
          name="calendar-filter-community"
          label="Community"
          value={filters.community}
          labelKey="name"
          filterKeys={["name"]}
          options={communities}
          getter={getCommunities}
          onChange={(value) => handleFilterChange("community", value)}
          sx={{ width: "100%" }}
        />
      </Box>

      <Box className={fieldClassName}>
        <MiDatePicker
          label="Due date"
          value={filters.dueDate?.toDate() ?? null}
          onChange={(date: Date | null) =>
            handleFilterChange("dueDate", date ? getDate(date) : null)
          }
        />
      </Box>
    </>
  );

  const buttons = (
    <Box className={isInline ? classes.inlineButtons : classes.stackedButtons}>
      <MiButton
        title="Reset filters"
        color={BUTTON_COLORS.BLACK}
        variant={BUTTON_VARIANTS.TEXT}
        disabled={!hasPendingFilters}
        onClick={() => {
          onResetFilters();
          onToggleFilters?.();
        }}
        className={cx(classes.underlined, !isInline && classes.fullWidthButton)}
      />
      <MiButton
        title="Apply filters"
        color={BUTTON_COLORS.PRIMARY}
        variant={BUTTON_VARIANTS.CONTAINED}
        disabled={!hasPendingFilters}
        onClick={onApplyFilters}
        className={cx(!isInline && classes.fullWidthButton)}
      />
    </Box>
  );

  return (
    <Box className={classes.fieldsRow}>
      {fields}
      {buttons}
    </Box>
  );
};

type CalendarFilterGroupProps = Pick<
  CalendarFilterGroupController,
  "filters" | "statusOptions" | "handleFilterChange"
> & {
  hasPendingFilters: boolean;
  onApplyFilters: () => void;
  onResetFilters: () => void;
  onToggleFilters?: () => void;
  layout?: "inline" | "stacked";
};

const useStyles = makeStyles()((theme) => ({
  fieldsRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1.5),
    alignItems: "flex-end",
  },
  inlineField: {
    display: "flex",
    flex: 1,
    minWidth: 140,
  },
  stackedField: {
    display: "flex",
    flex: 1,
    minWidth: 120,
    backgroundColor: theme.palette.common.white,
    borderRadius: 4,
  },
  inlineButtons: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
  },
  stackedButtons: {
    display: "flex",
    gap: theme.spacing(1),
  },
  fullWidthButton: {
    flex: 1,
  },
  underlined: {
    textDecoration: "underline",
  },
}));
